/**
 * @file This file contains the classes implementing the interfaces defined in the corresponding types file.
 * Note that the logic for dealing with the multi-table scenario is implemented in the sibling file MultiTable.ts.
 */

import type { Definition } from 'typescript-json-schema';
import type { smarttable } from 'sap/ui/comp/library';
import type { PopinLayout } from 'sap/m/library';
import type * as Edm from '@sap-ux/vocabularies-types/Edm';
import i18next from 'i18next';
import { ControlType, LogSeverity, PropertyMessageType, SchemaTag } from '@sap/ux-specification-types';
import type {
    AccessorPath,
    ConversionExit,
    ExtensionLogger,
    FileDataResult,
    GenerateAppSchemaParameters,
    ProcessingRule,
    ReferenceAdaptationInfo,
    SchemaHandlingParams,
    SyncRule,
    SyncRuleProvider
} from '@sap/ux-specification-types';
import * as v2 from '@sap/ux-specification-types/src/v2/genericSchemaHandling/controls/Table';
import type { FileData, SapUiGenericAppPageSettings } from '@sap/ux-specification-types/src/common';
import { addPatternForBindingChangeOfEnumLR, descriptionSrcURL, syncRule, validity } from '../../../common/decoration';
import { log } from '../../../../extensionLogger';
import { DataFieldTarget, getExportTablesPropertiesInfo, type LineItemInfo } from '../../generate/utils';
import type { MultiTableInfo } from './MultiTable';
import { getSyncRuleForMultiTableManifestProperty } from './MultiTable';
import { getSyncRuleForQuickVariantSelection } from './MultiViewSingleTable';
import {
    DISALLOWED_WITHOUT_EXPLANATION,
    getSyncRuleForManifestProperty,
    handleDisallowedProperty,
    hasPropertyTypeError
} from '../../generate/manifestPropertyUtils';
import type {
    DisallowedSpecification,
    ElementAdapterExtended,
    ManifestRule
} from '../../generate/manifestPropertyUtils';
import {
    addConversionExit,
    getDraftInfo,
    getManifestPathToPageSettings,
    getManifestPropertyByPath,
    isEmpty
} from '../../utils';
import { addEnumToSchema } from '../../../common';
import { adaptProcessingRuleForToolbar, type Toolbar } from './Action';
import { syncRuleMultiEdit } from './MultiEdit';

/**
 * Specific params applicable to the current table passed to the ProcessingRuleAdapter.
 *
 * @property settings - the component settings from the manifest
 * @property tableType - the type of the table
 * @property tableId - the control id of the smart table
 * @property multiTableInfo - Information about multi-table scenario if applicable, undefined if quickVariantSelectionX is not defined in the manifest
 * @property lineItemInfo - the line item this table is built on. For the instance representing the multi-table case as a whole it is the line item which is used as fallback.
 * @property entityType - the entity type this table is built for. For the instance representing the multi-table case as a whole it is the entityType of the main entitySet.
 */
export type TableParams = {
    settings?: SapUiGenericAppPageSettings;
    tableType: v2.TableTypeV2Enum;
    tableId: string;
    multiTableInfo?: MultiTableInfo;
    lineItemInfo?: LineItemInfo;
    entityType: Edm.EntityType;
    // facet?: FacetInfo; // information about the facet the table resides on (will be needed when this is extended to OP tables)
};

/**
 * This function should return the information whether the given entity type is analytical.
 * Unfortunately this is currently impossible due to missing information provided by the annotation api.
 * As soon as the annotation api has been enhanced accordingly, this function should be enhanced.
 *
 * @param entityTypeDefinition - the entity type to be checked
 * @returns true if the entity type is analytical, false otherwise
 */
function isTypeAnalytical(entityTypeDefinition: Edm.EntityType): boolean {
    return false; // todo: enhance
}

/**
 * Returns the entity set applicable for the given table parameters.
 *
 * @param tableParams - the parameters describing the table
 * @param generateParameters - the parameters used for schema generation
 * @returns the entity set for the given table
 */
function getEntitySet(
    tableParams: TableParams,
    generateParameters: GenerateAppSchemaParameters
): Edm.EntitySet | undefined {
    return tableParams.multiTableInfo?.variantInfo
        ? tableParams.multiTableInfo?.variantInfo.entitySetDefinition
        : generateParameters.entitySet;
}

/**
 * Returns the default table type used for tables for the given entity type (i.e. the table type which will be used by Fiori Elements if no manifest setting for the table type is available).
 *
 * @param entityTypeDefinition - the entity type for which the table is created
 * @returns the default table type for the given entity type
 */
export function getDefaultTableType(entityTypeDefinition: Edm.EntityType): v2.TableTypeV2Enum {
    const isAnalytical = isTypeAnalytical(entityTypeDefinition);
    return isAnalytical ? v2.TableTypeV2Enum.AnalyticalTable : v2.TableTypeV2Enum.ResponsiveTable;
}

/**
 * Returns the table type valid for the artifact described by given parameters.
 *
 * @param tableParams - the parameters describing the table
 * @returns the table type for the given table
 */
export function getTableType(tableParams: TableParams): v2.TableTypeV2Enum {
    if (tableParams.multiTableInfo?.variantInfo) {
        return tableParams.multiTableInfo.variantInfo.tableType;
    }
    const tableSettings = tableParams.settings?.tableSettings;
    const tableType =
        (tableSettings && tableSettings['type']) ||
        tableParams.settings?.tableType ||
        getDefaultTableType(tableParams.entityType);
    return tableType;
}

/**
 * Checks whether the table described by the given parameters is a responsive table.
 *
 * @param tableParams - the parameters describing the table
 * @returns true if the table is a responsive table, false otherwise
 */
function isResponsiveTable(tableParams: TableParams): boolean {
    return getTableType(tableParams) === v2.TableTypeV2Enum.ResponsiveTable;
}

/**
 * Checks whether the table described by the given parameters is a ui table.
 *
 * @param tableParams - the parameters describing the table
 * @returns true if the table is a ui table, false otherwise
 */
function isUiTable(tableParams: TableParams): boolean {
    return !isResponsiveTable(tableParams) && !tableParams.multiTableInfo?.variantInfo?.isChart;
}

/**
 * Get the tableSettings object from the manifest applicable to the specified table.
 *
 * @param tableParams - the parameters describing the table
 * @returns the tableSettings or undefined if no tableSettings are applicable
 */
function getTableSettings(tableParams: TableParams): object | undefined {
    return (
        (tableParams.multiTableInfo?.variantInfo &&
            tableParams.multiTableInfo.variantInfo.variantDefinition['tableSettings']) ||
        (tableParams.settings && tableParams.settings['tableSettings'])
    );
}

/**
 * Returns whether the specified table is configured a multi-selection table.
 *
 * @param tableParams - the parameters describing the table
 * @returns whether the table is multi-select
 */
function isMultiSelect(tableParams: TableParams): boolean {
    const tableSettings = getTableSettings(tableParams);
    return tableSettings?.['multiSelect'] === true;
}

/**
 * Checks whether the table described by the given parameters has selectAll switched on.
 *
 * @param tableParams - the parameters describing the table
 * @returns true if the table has selectAll switched on, false otherwise
 */
function isSelectAll(tableParams: TableParams): boolean {
    const tableSettings = getTableSettings(tableParams);
    return isMultiSelect(tableParams) && tableSettings['selectAll'] !== false; // note that tableSettings are available if isMultiSelect is true
}

/**
 * Checks whether a given table type is valid. Thereby undefined and empty string are also considered as valid (they represent the default table type).
 *
 * @param tableType - the table type to be checked
 * @param logger - would log an error if tableType is invalid
 * @returns - information whether the tableType is valid
 */
export function checkTableType(tableType: string | undefined, logger?: ExtensionLogger): boolean {
    if (
        tableType === undefined ||
        tableType === '' ||
        (Object.values(v2.TableTypeV2Enum) as string[]).includes(tableType)
    ) {
        return true;
    }
    log(logger, {
        severity: LogSeverity.Error,
        message: i18next.t('ILLEGALTABLETYPE', {
            property: tableType
        })
    });
    return false;
}

/**
 * Returns the information whether the tableParams are applicable to a certain SmartTable. This would not be the case
 * for tableParams belonging to the root node of a multi-table scenario or for views in a multi-table scenario which display a chart.
 *
 * @param tableParams the table params
 * @returns the information whether the tableParams are applicable to a certain SmartTable.
 */
function isForSmartTable(tableParams: TableParams): boolean {
    return !(
        (tableParams.multiTableInfo && !tableParams.multiTableInfo.variantInfo) ||
        tableParams.multiTableInfo?.variantInfo.isChart
    ); // the properties for the smart table are not applicable to a certain SmartTable on top-level of a multi-table scenario and on views which display a chart
}

/** SyncRule which should be used for all properties with artifact type 'FlexChange' belonging to the SmartTable. */
const syncRuleForFlexSmartTable: SyncRule = {
    flex: {
        controlType: (): ControlType => ControlType.SmartTable
    },
    processingRuleAdapter: function (processingRule: ProcessingRule, schemaHandlingParams: SchemaHandlingParams): void {
        const tableParams = schemaHandlingParams.specificParams as TableParams;
        if (isForSmartTable(tableParams)) {
            processingRule.controlId = tableParams.multiTableInfo
                ? tableParams.multiTableInfo.variantInfo.smartControlId
                : tableParams.tableId;
        } else {
            delete processingRule.element;
        }
    }
};

/**
 * Provide the syncRule for a property with artifact type 'FlexChange' belonging to the ResponsiveTable inside the SmartTable.
 *
 * @param addPatternForBinding information whether the addPatternForBindingChangeOfEnumLR should be added as generate property to the sync rule
 * @returns the syncRule
 */
function getSyncRuleForFlexResponsiveTable(addPatternForBinding: boolean): SyncRule {
    const syncRule: SyncRule = {
        flex: {
            controlType: (): ControlType => ControlType.Table
        },
        processingRuleAdapter: function (
            processingRule: ProcessingRule,
            schemaHandlingParams: SchemaHandlingParams
        ): void {
            const tableParams = schemaHandlingParams.specificParams as TableParams;
            if (
                (!tableParams.multiTableInfo || tableParams.multiTableInfo.variantInfo) &&
                isResponsiveTable(tableParams)
            ) {
                // Replicate logic from sap.suite.ui.generic.template.lib.StableIdDefinition, type: ListReport, subType: ResponsiveTable
                const suffix = tableParams.multiTableInfo
                    ? `-${tableParams.multiTableInfo.variantInfo.variantDefinition['key']}`
                    : '';
                processingRule.controlId = `responsiveTable${suffix}`;
            } else {
                delete processingRule.element;
            }
        }
    };
    if (addPatternForBinding) {
        syncRule.generate = addPatternForBindingChangeOfEnumLR; // add option for bindings (already executed in the generation of the generic schema)
    }
    return syncRule;
}

/**
 * Helper functions for implementing a ProcessingRuleAdapter for a manifest-based property belonging to the tableSettings.
 * Returns the manifest path for the table settings for the specified table params (which may either be global or for a specific view in a multi-table scenario).
 *
 * @param schemaHandlingParams - the schema handling parameters passed to the ProcessingRuleAdapter
 * @param tableParams - the parameters describing the table
 * @returns the manifest path to the tableSettings of the specified table
 */
export function getManifestPathForTableSettings(
    schemaHandlingParams: SchemaHandlingParams,
    tableParams: TableParams
): AccessorPath {
    const suffix = [];
    if (tableParams.multiTableInfo?.variantInfo) {
        suffix.push('quickVariantSelectionX', 'variants', tableParams.multiTableInfo.variantInfo.accessor);
    }
    suffix.push('tableSettings');
    const manifestPath = getManifestPathToPageSettings(schemaHandlingParams.pageInfo.pagePath, suffix);
    return manifestPath;
}

/**
 * This function adapts getSyncRuleForManifestProperty for properties belonging to the tableSettings of a table.
 *
 * @param property - the name of the property in tableSettings section of the manifest the SyncRule should be generated for
 * @param getDisallowReason - optional function to determine whether the property is disallowed for the given table params
 * @param adapt - an optional function that can be used to adapt the schema element representing the manifest property
 * @param provideReferenceAdaptation - an optional function that can be used to provide the ReferenceAdaptationInfo for the manifest property
 * @param expectedType - the expected type of the value of the property (if it is not undefined)
 * @returns the SyncRule for the tableSettings property
 */
function getSyncRuleForTableSettingsProperty(
    property: string,
    getDisallowReason?: (
        tableParams: TableParams,
        generateParameters: GenerateAppSchemaParameters,
        element: Definition
    ) => DisallowedSpecification | undefined,
    adapt?: ElementAdapterExtended,
    provideReferenceAdaptation?: (
        tableParams: TableParams,
        generateParameters: GenerateAppSchemaParameters
    ) => ReferenceAdaptationInfo | undefined,
    expectedType?: string
): SyncRule {
    // Construct the analyze function for getSyncRuleForManifestProperty
    const analyze = function (
        element: Definition,
        schemaHandlingParams: SchemaHandlingParams,
        generateParameters: GenerateAppSchemaParameters
    ): ManifestRule {
        const tableParams = schemaHandlingParams.specificParams as TableParams;
        let disallowedReason: DisallowedSpecification | undefined;
        // If the view is a chart view we can exclude the property generically. Otherwise check with function getDisallowedReason
        if (tableParams.multiTableInfo?.variantInfo?.isChart) {
            disallowedReason = i18next.t('NOCHARTPROPERTY', { property: property });
        } else if (getDisallowReason) {
            disallowedReason = getDisallowReason(tableParams, generateParameters, element);
        }
        const manifestPath = getManifestPathForTableSettings(schemaHandlingParams, tableParams);
        return { manifestPath, disallowedReason };
    };
    // Create a getReferenceAdaptation function with the correct signature for getSyncRuleForManifestProperty
    const provideReferenceAdaptationWrapper = provideReferenceAdaptation
        ? function (
              schemaHandlingParams: SchemaHandlingParams,
              generateParameters: GenerateAppSchemaParameters
          ): ReferenceAdaptationInfo | undefined {
              const tableParams = schemaHandlingParams.specificParams as TableParams;
              return provideReferenceAdaptation(tableParams, generateParameters);
          }
        : undefined;
    return getSyncRuleForManifestProperty(property, analyze, adapt, provideReferenceAdaptationWrapper, expectedType);
}

/**
 * This function provides a SyncRule for manifest properties which are only allowed on the top-level of the table definition and not on the level of views in a multi-table scenario.
 * Moreover, these properties possess a parent property that is a child of the page settings (so they are no direct children of the page settings).
 *
 * @param property - the property name in the manifest
 * @param noWorklistI18nKey - the i18n key specifying the warning message in case the property is used in a worklist scenario
 * @param syncRuleProvider - the sync rule provider for the specified manifest property
 * @returns the syncRule for the specified manifest property
 */
function getOnlyTopLevelNotWorklistSyncRule(
    property: string,
    noWorklistI18nKey: string,
    syncRuleProvider: SyncRuleProvider
): SyncRule {
    return {
        processingRuleAdapter(
            processingRule: ProcessingRule,
            schemaHandlingParams: SchemaHandlingParams,
            generateParameters: GenerateAppSchemaParameters
        ): void {
            const tableParams = schemaHandlingParams.specificParams as TableParams;
            if (tableParams.multiTableInfo?.variantInfo) {
                delete processingRule.element; // ignore this property on level of single views in multi-table scenario
                return;
            }
            if (tableParams.settings?.isWorklist) {
                const disallowedReason = i18next.t(noWorklistI18nKey);
                const manifestPath = getManifestPathToPageSettings(schemaHandlingParams.pageInfo.pagePath, []);
                const { value } = getManifestPropertyByPath(generateParameters.manifest, manifestPath, property);
                handleDisallowedProperty(disallowedReason, processingRule, value, manifestPath);
                return;
            }
            processingRule.referenceAdaptation = {
                syncRuleProvider: syncRuleProvider
            };
        }
    };
}

/**
 * Modifies a schema element such that the fields of the given entity type are added as enumeration values.
 *
 * @param element - the schema element to be modified
 * @param entityTypeDefinition - the entity type hosting the fields
 */
export function addEntityTypeFieldsAsEnum(element: Definition, entityTypeDefinition: Edm.EntityType): void {
    const itemsEnum = entityTypeDefinition.entityProperties.map((property) => property.name);
    element.items = {
        type: 'string',
        enum: itemsEnum
    };
}

export class DataLoadSettings {
    // loadDataOnAppLaunch
    @descriptionSrcURL('https://ui5.sap.com/sdk/#/topic/b736ab6ae3654789ba1f9630e5720af3?q=loadDataOnAppLaunch')
    @syncRule({
        manifest: {},
        processingRuleAdapter(processingRule: ProcessingRule, schemaHandlingParams: SchemaHandlingParams): void {
            const manifestPath = getManifestPathToPageSettings(schemaHandlingParams.pageInfo.pagePath, [
                'dataLoadSettings'
            ]);
            processingRule.manifestPath = manifestPath;
            const tableParams = schemaHandlingParams.specificParams as TableParams;
            if (tableParams.multiTableInfo && !tableParams.multiTableInfo.variantInfo) {
                processingRule.element.description += '\n' + i18next.t('LOADDATAONAPPLAUNCHMULTITABLE'); // add information to the description providing specific information in multi-table scenarios
            }
        }
    })
    @validity({
        since: '1.75.2'
    })
    loadDataOnAppLaunch: v2.LoadDataOnAppLaunchSettingsEnum;
}

export class CreateWithFilters implements v2.CreateWithFilters {
    // strategy
    @descriptionSrcURL('https://ui5.sap.com/sdk/#/topic/189e2d8a59f04a2693a4fde7f4a91a65?q=createWithFilters')
    @syncRule({
        manifest: {},
        processingRuleAdapter(processingRule: ProcessingRule, schemaHandlingParams: SchemaHandlingParams): void {
            const manifestPath = getManifestPathToPageSettings(schemaHandlingParams.pageInfo.pagePath, [
                'createWithFilters'
            ]);
            processingRule.manifestPath = manifestPath;
        }
    })
    strategy: v2.StrategyEnum;
}

export class Fields4CreateWithParametersDialog implements v2.Fields4CreateWithParametersDialog {
    // fields
    @validity({
        since: '1.77.0'
    })
    @syncRule({
        processingRuleAdapter(processingRule: ProcessingRule, schemaHandlingParams: SchemaHandlingParams): void {
            const entityTypeDefinition = schemaHandlingParams.specificParams as Edm.EntityType;
            addEntityTypeFieldsAsEnum(processingRule.element, entityTypeDefinition);
        }
    })
    fields: string[];
}

export class Table implements v2.Table {
    // entitySet
    @syncRule(getSyncRuleForMultiTableManifestProperty('entitySet'))
    @descriptionSrcURL('https://ui5.sap.com/sdk/#/topic/6698b80fc1a543ebb5c07e0781e9b93e')
    entitySet?: string;

    // annotationPath
    @syncRule(getSyncRuleForMultiTableManifestProperty('annotationPath'))
    @descriptionSrcURL('https://ui5.sap.com/sdk/#/topic/97dfeea4a8c346368cea6bf7a3e85478')
    annotationPath?: string;

    // showItemNavigationOnChart
    @syncRule(getSyncRuleForMultiTableManifestProperty('showItemNavigationOnChart'))
    showItemNavigationOnChart?: boolean;

    // views
    @syncRule(getSyncRuleForMultiTableManifestProperty('views'))
    views?: v2.MultiViewsDefinition;

    // quickVariantSelection
    @syncRule(getSyncRuleForQuickVariantSelection())
    quickVariantSelection?: v2.MultiViewsOnTable;

    // showTablePersonalisation
    @syncRule(syncRuleForFlexSmartTable)
    showTablePersonalisation?: boolean;

    // exportType
    @syncRule(syncRuleForFlexSmartTable)
    exportType?: smarttable.ExportType;

    // useExportToExcel
    @syncRule({
        flex: {
            controlType: (): ControlType => ControlType.SmartTable
        },
        processingRuleAdapter(
            processingRule: ProcessingRule,
            schemaHandlingParams: SchemaHandlingParams,
            generateParameters: GenerateAppSchemaParameters
        ): void {
            // This property is outdated by enableExport, but if it is used we still want to show it in the schema for compatibility reasons.
            const tableParams = schemaHandlingParams.specificParams as TableParams;
            if (isForSmartTable(tableParams)) {
                const smartControlId = tableParams.multiTableInfo
                    ? tableParams.multiTableInfo.variantInfo.smartControlId
                    : tableParams.tableId;
                const { message } = getExportTablesPropertiesInfo(generateParameters.flex || [], smartControlId);
                if (message) {
                    processingRule.element[SchemaTag.messages] = [message];
                } else {
                    processingRule.element[SchemaTag.hidden] = true;
                }
                processingRule.controlId = smartControlId;
            } else {
                // property not applicable to a SmartTable
                delete processingRule.element;
            }
        }
    })
    useExportToExcel?: boolean;

    // enableExport
    @validity({
        since: '1.145.0'
    })
    @syncRule(syncRuleForFlexSmartTable)
    enableExport?: boolean;

    // growingThreshold
    @syncRule(getSyncRuleForFlexResponsiveTable(false))
    growingThreshold?: number;

    // popinLayout
    @syncRule(getSyncRuleForFlexResponsiveTable(true))
    popinLayout?: PopinLayout;

    // includeItemInSelection
    @syncRule(getSyncRuleForFlexResponsiveTable(false))
    includeItemInSelection?: boolean;

    // tableType
    @syncRule(
        getSyncRuleForManifestProperty(
            'tableType',
            // analyze:
            function (element: Definition, schemaHandlingParams: SchemaHandlingParams): ManifestRule {
                const tableParams = schemaHandlingParams.specificParams as TableParams;
                const manifestPath = getManifestPathToPageSettings(schemaHandlingParams.pageInfo.pagePath);
                // Property tableType is obsolete. For views in a multi-table scenario it was even never supported.
                const disallowedReason = tableParams.multiTableInfo?.variantInfo
                    ? DISALLOWED_WITHOUT_EXPLANATION
                    : i18next.t('PROPERTYOBSOLETE', { property: 'tableType' });
                return {
                    manifestPath,
                    disallowedReason
                };
            } // analyze
        )
    )
    tableType?: v2.TableTypeV2Enum;

    // type
    @syncRule(
        getSyncRuleForTableSettingsProperty(
            'type',
            undefined,
            // adapt:
            function (element: Definition, schemaHandlingParams: SchemaHandlingParams): void {
                const tableParams = schemaHandlingParams.specificParams as TableParams;
                // defaultTypeForMultiView is only set when we are on variant level and the generic level explicitly sets a type
                const defaultTypeForMultiView =
                    tableParams.multiTableInfo?.variantInfo && tableParams.settings?.tableSettings
                        ? tableParams.settings.tableSettings['type']
                        : '';
                if (
                    tableParams.multiTableInfo?.variantInfo &&
                    tableParams.multiTableInfo.usedTableTypes.nonResponsive > 0 &&
                    tableParams.multiTableInfo.usedTableTypes.responsive > 0
                ) {
                    // We have an illegal combination of responsive and non-responsive table types in a multi-view setting.
                    // We will add this as an error to all instances that defer from the default.
                    const defaultTableType = defaultTypeForMultiView || getDefaultTableType(tableParams.entityType);
                    const isDefaultResponsive = defaultTableType === v2.TableTypeV2Enum.ResponsiveTable;
                    if (isResponsiveTable(tableParams) !== isDefaultResponsive) {
                        // This is a variant which defers from the default -> consider it as being a reason for the incorrect setup
                        element[SchemaTag.messages] = [
                            {
                                text: i18next.t(
                                    isDefaultResponsive ? 'MULTITABLEONLYRESPONSIVE' : 'MULTITABLENORESPONSIVE'
                                ),
                                type: PropertyMessageType.Warning
                            }
                        ];
                    }
                }
                // The generic schema element points to TableTypeV2Enum which provides all possible table types as an enumeration.
                // In the following cases we need to reduce this enumeration:
                // 1. type for one view in a multi-table scenario in order to avoid combinations of responsive and non-responsive table types
                // 2. Remove AnalyticalTable in case the entity type is not analytical
                // As we cannot determine whether an entity type is analytical yet, we ignore the second case.
                if (tableParams.multiTableInfo?.variantInfo /* || !isTypeAnalytical(tableParams.entityType) */) {
                    delete element.$ref; // Remove reference to TableTypeV2Enum
                    const enumEntries = [];
                    // When
                    // - dealing with a variant of a multi-view scenario and
                    // - the generic level sets a non-responsive type as default and
                    // - there is indeed no responsive table variant yet and
                    // - there is at least one other non-responsive table variant
                    // In this case changing the type to responsive would automatically break the app, since it creates a mixture of responsive and non-responsive tables.
                    // => Suppress responsive from the value-help. If the user really wants to change all variants to responsive he should first remove the explicit generic setting defaulting to non-responsive.
                    if (
                        !defaultTypeForMultiView ||
                        defaultTypeForMultiView === v2.TableTypeV2Enum.ResponsiveTable ||
                        tableParams.multiTableInfo.usedTableTypes.responsive > 0 ||
                        tableParams.multiTableInfo.usedTableTypes.nonResponsive < 2
                    ) {
                        // In all other cases add 'responsive' to the value-help
                        enumEntries.push(v2.TableTypeV2Enum.ResponsiveTable);
                    }
                    if (
                        defaultTypeForMultiView !== v2.TableTypeV2Enum.ResponsiveTable ||
                        tableParams.multiTableInfo.usedTableTypes.nonResponsive > 0 ||
                        tableParams.multiTableInfo.usedTableTypes.responsive < 2
                    ) {
                        // Same as above, interchanging the roles of responsive and non-responsive.
                        enumEntries.push(v2.TableTypeV2Enum.GridTable);
                        //if (isAnalytical(tableParams.entityType)){ // todo: activate this condition
                        enumEntries.push(v2.TableTypeV2Enum.AnalyticalTable);
                        //}
                        enumEntries.push(v2.TableTypeV2Enum.TreeTable);
                    }
                    addEnumToSchema(enumEntries, element);
                }
            }, // adapt
            undefined,
            'string'
        )
    )
    @descriptionSrcURL('https://ui5.sap.com/sdk/#/topic/5d270547f113468e83e06dd7ee408a45')
    type?: v2.TableTypeV2Enum;

    // condensedTableLayout
    @syncRule(
        getSyncRuleForManifestProperty(
            'condensedTableLayout',
            // analyze:
            function (element: Definition, schemaHandlingParams: SchemaHandlingParams): ManifestRule {
                const tableParams = schemaHandlingParams.specificParams as TableParams;
                const manifestPath = getManifestPathToPageSettings(schemaHandlingParams.pageInfo.pagePath);
                let disallowedReason: DisallowedSpecification | undefined;
                if (tableParams.multiTableInfo?.variantInfo) {
                    disallowedReason = DISALLOWED_WITHOUT_EXPLANATION; // Setting is not allowed on level of views. Hence, ignore it here without any feedback.
                } else if (isResponsiveTable(tableParams)) {
                    disallowedReason = i18next.t('CONDENSEDTABLELAYOUTNOEFFECT');
                }
                return {
                    manifestPath,
                    disallowedReason
                };
            } // analyze
        )
    )
    condensedTableLayout?: boolean;

    // enableTableFilterInPageVariant
    @syncRule(
        getSyncRuleForManifestProperty(
            'enableTableFilterInPageVariant',
            // analyze:
            function (element: Definition, schemaHandlingParams: SchemaHandlingParams): ManifestRule {
                const tableParams = schemaHandlingParams.specificParams as TableParams;
                const manifestPath = getManifestPathToPageSettings(schemaHandlingParams.pageInfo.pagePath);
                let disallowedReason: DisallowedSpecification | undefined;
                if (tableParams.multiTableInfo?.variantInfo) {
                    disallowedReason = DISALLOWED_WITHOUT_EXPLANATION; // Setting is not allowed on level of views. Hence, ignore it here without any feedback.
                } else if (tableParams.settings?.isWorklist) {
                    disallowedReason = i18next.t('ENABLETABLEFILTERINPAGEVARIANTWORKLIST');
                } else if (tableParams.settings?.smartVariantManagement === false) {
                    disallowedReason = i18next.t('ENABLETABLEFILTERINPAGEVARIANTNOEFFECT');
                }
                return {
                    manifestPath,
                    disallowedReason
                };
            } // analyze
        )
    )
    enableTableFilterInPageVariant?: boolean;

    // inlineDelete
    @syncRule(
        getSyncRuleForTableSettingsProperty(
            'inlineDelete',
            // getDisallowReason
            function (tableParams: TableParams): string | undefined {
                return isResponsiveTable(tableParams) ? undefined : i18next.t('INLINEDELETEONLYRESPONSIVE');
            }, // getDisallowedReason
            //adapt:
            function (
                element: Definition,
                schemaHandlingParams: SchemaHandlingParams,
                generateParameters: GenerateAppSchemaParameters,
                value: unknown
            ): void {
                if (value === true && isMultiSelect(schemaHandlingParams.specificParams as TableParams)) {
                    element[SchemaTag.messages] = [
                        {
                            text: i18next.t('NOINLINEDELETEANDMULTISELECT'),
                            deletable: true,
                            type: PropertyMessageType.Warning
                        }
                    ];
                }
            } // adapt
        )
    )
    inlineDelete?: boolean;

    // multiSelect
    @syncRule(
        getSyncRuleForTableSettingsProperty(
            'multiSelect',
            undefined, // getDisallowedReason
            //adapt:
            function (
                element: Definition,
                schemaHandlingParams: SchemaHandlingParams,
                generateParameters: GenerateAppSchemaParameters,
                value: unknown
            ): void {
                // Problematic scenario: multiSelect and inlineDelete switched on at the same time. Can only happen for responsive tables as inlineDelete is only available for responsive tables.
                const tableParams = schemaHandlingParams.specificParams as TableParams;
                if (value !== true || !isResponsiveTable(tableParams)) {
                    return;
                }
                const tableSettings = getTableSettings(tableParams);
                if (tableSettings?.['inlineDelete'] === true) {
                    element[SchemaTag.messages] = [
                        {
                            text: i18next.t('NOINLINEDELETEANDMULTISELECT'),
                            deletable: true,
                            type: PropertyMessageType.Warning
                        }
                    ];
                }
            } // adapt
        )
    )
    @descriptionSrcURL('https://ui5.sap.com/sdk/#/topic/402fac73b4794a31983609ebdde580d2')
    multiSelect?: boolean;

    // selectAll
    @syncRule(
        getSyncRuleForTableSettingsProperty(
            'selectAll',
            // getDisallowReason:
            function (
                tableParams: TableParams,
                generateParameters: GenerateAppSchemaParameters,
                element: Definition
            ): string | undefined {
                if (!isMultiSelect(tableParams)) {
                    // In this case the function returns a disallowedReason. Therefore, the adapt function will not be called.
                    // Still the property may be shown to the user (in case it contains a value).
                    // The description contains markups that allow the adapt function (below) to provide the correct text depending on the table type.
                    // For the warning scenario we simply remove this part.
                    element.description = element.description.split('\n')[0];
                    return i18next.t('SELECTALLNOMULTISELECT');
                }
            }, // getDisallowReason
            // adapt:
            function (element: Definition, schemaHandlingParams: SchemaHandlingParams): void {
                // add table type dependent information to the description of the property.
                const tableParams = schemaHandlingParams.specificParams as TableParams;
                const i18nForDefaultInfo = isUiTable(tableParams)
                    ? 'SELECTALLDEFAULTINFOUITABLE'
                    : 'SELECTALLDEFAULTINFORESPONSIVETABLE';
                element.description += '\n' + i18next.t(i18nForDefaultInfo);
            } // adapt
        )
    )
    selectAll?: boolean;

    // selectionLimit
    @syncRule(
        getSyncRuleForTableSettingsProperty('selectionLimit', function (tableParams: TableParams): string | undefined {
            if (!isUiTable(tableParams) || !isMultiSelect(tableParams)) {
                return i18next.t('SELECTIONLIMITPREREQUISIT');
            }
            if (isSelectAll(tableParams)) {
                return i18next.t('SELECTIONLIMITNOSELECTALL');
            }
        })
    )
    @descriptionSrcURL('https://ui5.sap.com/sdk/#/topic/116b5d82e8c545e2a56e1b51b8b0a9bd?q=selectionLimit')
    selectionLimit?: number;

    // copy
    @syncRule(getSyncRuleForTableSettingsProperty('copy'))
    @validity({
        since: '1.120.0'
    })
    @descriptionSrcURL('https://ui5.sap.com/sdk/#/topic/c0f6592a592e47f9bb6d09900de47412')
    copy?: boolean;

    // dataLoadSettings
    @syncRule(getOnlyTopLevelNotWorklistSyncRule('dataLoadSettings', 'NODATALOADSETTINGSINWORKLIST', DataLoadSettings))
    dataLoadSettings?: v2.DataLoadSettings;

    // createWithFilters
    @syncRule(
        getOnlyTopLevelNotWorklistSyncRule('createWithFilters', 'NOCREATEWITHFILTERSINWORKLIST', CreateWithFilters)
    )
    createWithFilters?: v2.CreateWithFilters;

    // createWithParameterDialog
    @validity({
        since: '1.77.0'
    })
    @descriptionSrcURL('https://ui5.sap.com/sdk/#/topic/2d04f60da84a49f6bb8617fbf1d3d2aa')
    @syncRule(
        getSyncRuleForTableSettingsProperty(
            'createWithParameterDialog',
            // getDisallowReason:
            function (
                tableParams: TableParams,
                generateParameters: GenerateAppSchemaParameters
            ): DisallowedSpecification | undefined {
                const entitySet = getEntitySet(tableParams, generateParameters);
                if (!entitySet) {
                    return DISALLOWED_WITHOUT_EXPLANATION;
                }
                const draftInfo = getDraftInfo(entitySet);
                if (draftInfo.isDraft) {
                    return i18next.t('CREATEWITHDIALOGDRAFT');
                }
            }, // getDisallowReason
            // adapt:
            function (
                element: Definition,
                schemaHandlingParams: SchemaHandlingParams,
                generateParameters: GenerateAppSchemaParameters,
                value: unknown
            ) {
                addConversionExit(element, 'createWithParameterDialog', value);
            }, // adapt
            // provideReferenceAdaptation:
            function (tableParams: TableParams): ReferenceAdaptationInfo | undefined {
                return {
                    syncRuleProvider: Fields4CreateWithParametersDialog,
                    specificParams: tableParams.multiTableInfo?.variantInfo
                        ? tableParams.multiTableInfo.variantInfo.entityTypeDefinition
                        : tableParams.entityType,
                    suffix: tableParams.multiTableInfo?.variantInfo
                        ? tableParams.multiTableInfo.variantInfo.iconTabFilterKey
                        : undefined
                };
            }, // provideReferenceAdaptation
            'object'
        )
    )
    createWithParameterDialog?: v2.Fields4CreateWithParametersDialog;

    // multiEdit
    @validity({
        since: '1.98.0'
    })
    @descriptionSrcURL('https://ui5.sap.com/sdk/#/topic/7cc4f04364c540c397130ff92f84c3ca')
    @syncRule(syncRuleMultiEdit)
    multiEdit?: v2.MultiEdit;

    // toolbar
    @syncRule({
        processingRuleAdapter(processingRule: ProcessingRule, schemaHandlingParams: SchemaHandlingParams): void {
            const tableParams = schemaHandlingParams.specificParams as TableParams;
            if (tableParams.multiTableInfo && !tableParams.multiTableInfo.variantInfo) {
                delete processingRule.element; // no toolbar defined in the global table settings in the multi-table case
                return;
            }
            processingRule.element[SchemaTag.propertyIndex] = 0;
            const lineItemInfo: LineItemInfo = tableParams.multiTableInfo
                ? tableParams.multiTableInfo.variantInfo.lineItemInfo
                : tableParams.lineItemInfo;
            const isChart = tableParams.multiTableInfo?.variantInfo?.isChart;
            const iconTabFilterKey = tableParams.multiTableInfo?.variantInfo?.iconTabFilterKey;
            adaptProcessingRuleForToolbar(
                processingRule,
                DataFieldTarget.Toolbar,
                lineItemInfo?.dataFieldInfos ?? [],
                isChart,
                iconTabFilterKey
            );
        }
    })
    toolbar?: Toolbar;
} // Table

/**
 * ConversionExit for property createWithParameterDialog of Table control.
 * Converts between the manifest representation (object with field names as keys) and the config representation (object with array of field names).
 */
export const CREATE_WITH_PARAMETER_DIALOG: ConversionExit = {
    appToConfig(
        appValue: unknown,
        parameters: unknown,
        fragments: FileData[],
        definition: Definition,
        logger?: ExtensionLogger
    ): unknown {
        const hasTypeError = hasPropertyTypeError(appValue, 'fields', logger, 'object');
        return appValue && !hasTypeError
            ? {
                  fields: Object.keys(appValue['fields'])
              }
            : undefined;
    },
    configToApp(
        configValue: unknown,
        parameters: unknown,
        fragments: FileDataResult[],
        definition: Definition,
        oldAppValue: unknown
    ): unknown {
        const configFields = configValue?.['fields'];
        if (Array.isArray(configFields)) {
            const newAppValue = oldAppValue && typeof oldAppValue === 'object' ? { ...oldAppValue } : {};
            if (configFields.length === 0) {
                delete newAppValue['fields'];
                return isEmpty(newAppValue) ? undefined : newAppValue;
            }
            const fields = {}; // the new entry for the fields-property
            const fieldsAsArray = configFields as string[];
            fieldsAsArray.forEach(function (field) {
                fields[field] = { path: field };
            });
            newAppValue['fields'] = fields;
            return newAppValue;
        }
        return oldAppValue; // the fields-property in configValue was untouched (since every change would have converted it into an array). Hence, keep the old value.
    }
};
